/**
 * How a crew is named on screen: the company, with the contact beside it.
 *
 * Jonathon, 2026-10-04: "update the crews to where it shows the company's
 * name and then the contact in parenthesis beside it. this needs to be
 * reflected everywhere."
 *
 * Until then a crew had one name box, and it held whatever somebody typed on
 * the day — "Brad Kiley - Apex Metal" on one row, "Feliciano's Roof & Paint,
 * LLC" on the next, with Feliciano himself only on the pay-to line. So now a
 * crew has a company and a contact, and everywhere a crew is named — a work
 * order's dropdown, the calendar, the payout board, the Thursday reminder,
 * the inspector picker — goes through this one function, so the shape is the
 * same on every screen.
 *
 * Pure, and tolerant of partial rows: most screens fetch only the columns
 * they need, and a crew with no contact yet is simply its company.
 */

/** The two columns this reads. Everything else on `crews` is irrelevant. */
export interface CrewNamed {
  name?: string | null;
  contact_name?: string | null;
}

/**
 * "Apex Metal (Brad Kiley)"; "Pedro Gomez" when there is no contact, or the
 * contact is the company again; null when there is no crew or no name at
 * all, so a caller can fall back to its own wording ("—", "no crew on the
 * order") rather than printing an empty bracket.
 */
export function crewLabel(crew: CrewNamed | null | undefined): string | null {
  const company = (crew?.name ?? "").trim();
  if (company === "") return null;
  const contact = (crew?.contact_name ?? "").trim();
  if (contact === "" || contact.toLowerCase() === company.toLowerCase()) {
    return company;
  }
  return `${company} (${contact})`;
}
